import type { WidaStudent } from '../Upload/widaHistory';
import { loadPlanRoster } from '../Upload/studentRoster';
import { assessmentScale } from '../IndividualPlan/derive';
import { altLevelIndex } from '../IndividualPlan/altPlanContent';
import {
  findSisInRoster,
  loadSisRoster,
  type SisStudent,
} from '../Upload/sisRoster';
import { formatGradeOrdinal } from '../Roster/rosterRows';
import type { RosterClassificationRow } from '../Roster/rosterClassification';

export { formatGradeOrdinal };

export type PlanRow = {
  id: string;
  localId: string;
  name: string;
  school: string;
  elTeacher: string;
  grade: string;
  accessComposite: string;
  generatedOn: string;
};

export const EL_TEACHER_PLACEHOLDER = 'SIS Data Needed';

export function formatGeneratedOn(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  });
}

function latestComposite(student: WidaStudent): number | null {
  const nonWaived = student.years.filter((y) => !y.waived);
  const last = nonWaived[nonWaived.length - 1];
  return last ? last.composite.level : null;
}

export function planStudentName(student: WidaStudent): string {
  const last = student.lastName.trim();
  const first = student.firstName.trim();
  if (last && first) return `${last}, ${first}`;
  if (last || first) return last || first;
  return student.fullName.trim() || `Student ${student.id}`;
}

function localIdFor(student: WidaStudent, sis: SisStudent | null): string {
  const own = student.localStudentId.trim();
  if (own || !sis) return own;
  const known = new Set(
    [student.id, student.stateStudentId].map((v) => v.trim()).filter(Boolean),
  );
  return (
    [sis.studentId, ...sis.altIds]
      .map((v) => v.trim())
      .find((v) => v !== '' && !known.has(v)) ?? ''
  );
}

export function widaStudentToPlanRow(
  student: WidaStudent,
  generatedOn: string,
  sis: SisStudent | null = null,
): PlanRow {
  const composite = latestComposite(student);
  const alt = assessmentScale(student) === 'alternate_access';
  return {
    id: student.id,
    localId: localIdFor(student, sis),
    name: planStudentName(student),
    school: (sis?.school ?? '').trim() || student.latestSchool.trim() || '—',
    elTeacher: (sis?.elTeacher ?? '').trim() || EL_TEACHER_PLACEHOLDER,
    grade: formatGradeOrdinal((sis?.grade ?? '').trim() || student.latestGrade),
    accessComposite: alt
      ? composite == null
        ? 'Alt · —'
        : `Alt · L${altLevelIndex(composite)}`
      : composite == null
        ? '—'
        : composite.toFixed(1),
    generatedOn,
  };
}

export function sortByPlanName(students: WidaStudent[]): WidaStudent[] {
  return [...students].sort((a, b) =>
    planStudentName(a).localeCompare(planStudentName(b)),
  );
}

export function rosterPlanRows(generatedOn: string): PlanRow[] {
  const sisRoster = loadSisRoster();
  return loadPlanRoster().map((s) =>
    widaStudentToPlanRow(
      s,
      generatedOn,
      findSisInRoster([s.id, s.stateStudentId, s.localStudentId], sisRoster),
    ),
  );
}

function serverStudentName(row: RosterClassificationRow): string {
  const last = (row.lastName ?? '').trim();
  const first = (row.firstName ?? '').trim();
  if (last && first) return `${last}, ${first}`;
  if (last || first) return last || first;
  return `Student ${row.studentKey}`;
}

export function classificationToPlanRow(
  row: RosterClassificationRow,
  generatedOn: string,
): PlanRow {
  const teachers = (row.elTeachers ?? []).map((t) => t.trim()).filter(Boolean);
  const composite = row.compositeProficiency ?? null;
  const alternate = row.assessmentScale === 'alternate_access';
  return {
    // A plan saved before this listing existed is filed under the key the WIDA
    // file used, and neither the route nor the store canonicalises aliases, so
    // linking by the assembled key would hide it and then write a second one.
    id: (row.widaKey ?? '').trim(),
    localId: (row.localId ?? '').trim(),
    name: serverStudentName(row),
    school: (row.school ?? '').trim() || '—',
    elTeacher: teachers.length > 0 ? teachers.join(', ') : EL_TEACHER_PLACEHOLDER,
    grade: formatGradeOrdinal(row.grade ?? ''),
    // The label follows the assessment the score is on, which the server
    // states. The roster's alternate designation is a different fact and does
    // not make a standard ACCESS score an alternate one.
    accessComposite: alternate
      ? composite == null
        ? 'Alt · —'
        : `Alt · L${altLevelIndex(composite)}`
      : composite == null
        ? '—'
        : composite.toFixed(1),
    generatedOn,
  };
}

// Assembly appends a roster row no WIDA file scored as a student of its own,
// with no WIDA key. There is no plan behind such a row, so it is left out here
// rather than listed and then dead-ended by the plan route.
export function classificationPlanRows(
  rows: RosterClassificationRow[],
  generatedOn: string,
): PlanRow[] {
  return rows
    .filter((row) => (row.widaKey ?? '').trim() !== '' && row.heldOut !== true)
    .map((row) => classificationToPlanRow(row, generatedOn));
}
